import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import {ArbeitszeitService, BauvorhabenWithID} from '../../../../../services/openapi';
import APIWrapperContext from '../../../../../services/APIWrapperContext';
import List from '@mui/material/List';
import AddIcon from '@mui/icons-material/Add';
import BauvorhabenAnlegen from './BauvorhabenAnlegen';
import BauvorhabenPaginationControls from './BauvorhabenPaginationControls';
import BauvorhabenListItem from './BauvorhabenListItem';
import DeleteBauvorhabenDialog from './DeleteBauvorhabenDialog';
import { Settings } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface IZeiterfassungSelectBauvorhabenProps {}

type SortField = 'bvh_name' | 'mandnr' | 'projektnr';
type SortDirection = 'asc' | 'desc';

export default function BauvorhabenList(props: IZeiterfassungSelectBauvorhabenProps) {
  const {} = props;
  const {call_backend_api} = React.useContext(APIWrapperContext);
  const [bauvorhabenList, setBauvorhabenList] = React.useState<BauvorhabenWithID[]>([]);
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [name, setName] = React.useState('');
  const [id, setId] = React.useState(0);
  const {t} = useTranslation();

  // Modal state
  const [openNewModal, setOpenNewModal] = React.useState(false);

  // New Bauvorhaben form state
  const [mandant, setMandant] = React.useState<string>('');
  const [projektnummerText, setProjektnummerText] = React.useState<string>('');
  const [projektnummer, setProjektnummer] = React.useState<number>(-1);
  const [nameBauvorhaben, setNameBauvorhaben] = React.useState<string>('');

  // Search and sort state
  const [searchTerm, setSearchTerm] = React.useState('');
  const [sortField, setSortField] = React.useState<SortField>('bvh_name');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc');
  const [page, setPage] = React.useState(0);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);

  const loadBauvorhaben = () => {
    call_backend_api(
      ArbeitszeitService.getAlleBauvorhabenArbeitzeitBauvorhabenGet(),
      (t: BauvorhabenWithID[]) => {
        setBauvorhabenList(t);
      },
      undefined,
      undefined,
      false,
      undefined,
      undefined
    );
  };

  React.useEffect(() => {
    loadBauvorhaben();
  }, []);

  const handleClose = () => {
    setName('');
    setOpen(false);
    setError(false);
  };

  const handleDelete = (confirm = false) => {
    call_backend_api(
      ArbeitszeitService.createBauvorhabenArbeitzeitBauvorhabenDelete(id, confirm),
      () => {
        setBauvorhabenList([...bauvorhabenList.filter((b) => b.id !== id)]);
        setOpen(false);
        setId(0);
        setName('');
      },
      (e: any) => {
        if (e.status === 409) {
          setError(true);
        }
      },
      undefined,
      false,
      undefined,
      undefined
    );
  };

  // Form validation functions
  function containsOnlyDigits(str: string) {
    return /^\d+$/.test(str);
  }

  const evalErrorProjektnummer = (projektnummer: string) => {
    if (projektnummer.length > 0) {
      return containsOnlyDigits(projektnummer);
    }
    return false;
  };

  const evalReadyForCreate = () => {
    if (
      mandant.length > 0 &&
      projektnummer > 0 &&
      nameBauvorhaben.length > 0 &&
      evalErrorProjektnummer(projektnummerText)
    ) {
      return true;
    }
    return false;
  };

  // Form handlers
  const handleMandant = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMandant(event.target.value);
  };

  const handleProjektnummer = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProjektnummerText(event.target.value);
    if (evalErrorProjektnummer(event.target.value)) {
      setProjektnummer(parseInt(event.target.value));
    }
  };

  const handleNameBauvorhaben = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNameBauvorhaben(event.target.value);
  };

  const handleOpenNewModal = () => {
    setOpenNewModal(true);
  };

  const handleCloseNewModal = () => {
    setBauvorhabenList([]);
    setTimeout(() => {
      loadBauvorhaben();
      setOpenNewModal(false);
    }, 100);
    setMandant('');
    setProjektnummerText('');
    setProjektnummer(-1);
    setNameBauvorhaben('');
  };

  // Filtering function
  const filteredBauvorhaben = React.useMemo(() => {
    return bauvorhabenList.filter((item) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        item.bvh_name.toLowerCase().includes(searchLower) ||
        item.mandnr.toString().toLowerCase().includes(searchLower) ||
        item.projektnr.toString().toLowerCase().includes(searchLower)
      );
    });
  }, [bauvorhabenList, searchTerm]);

  // Sorting function
  const sortedBauvorhaben = React.useMemo(() => {
    return [...filteredBauvorhaben].sort((a, b) => {
      let comparison = 0;
      if (sortField === 'bvh_name') {
        comparison = a.bvh_name.localeCompare(b.bvh_name);
      } else if (sortField === 'mandnr') {
        comparison = a.mandnr - b.mandnr;
      } else if (sortField === 'projektnr') {
        comparison = a.projektnr.toString().localeCompare(b.projektnr.toString());
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredBauvorhaben, sortField, sortDirection]);

  // Pagination
  const paginatedBauvorhaben = React.useMemo(() => {
    const startIndex = page * itemsPerPage;
    return sortedBauvorhaben.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedBauvorhaben, page, itemsPerPage]);

  const totalPages = Math.ceil(sortedBauvorhaben.length / itemsPerPage);

  // Handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page on search
  };

  const handleSortChange = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleItemsPerPageChange = (event: any) => {
    setItemsPerPage(Number(event.target.value));
    setPage(0); // Reset to first page when changing items per page
  };

  return (
    <>
      <Card>
        <CardHeader
          title={<Typography variant='h4'>{t('all_projects')}</Typography>}
          action={
            <Box sx={{display: 'flex', gap: 2}}>
              <Button
                variant='contained'
                color='primary'
                startIcon={<AddIcon />}
                onClick={handleOpenNewModal}
                sx={{height: '40px'}}>
                {t('add_project')}
              </Button>
            </Box>
          }
        />

        <CardContent>
          <List
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              overflow: 'hidden',
              p: 1,
            }}>
            {paginatedBauvorhaben.map((el) => {
              return (
                <BauvorhabenListItem
                  key={el.id}
                  item={el}
                  onDelete={(id, name) => {
                    setName(name);
                    setId(id);
                    setOpen(true);
                  }}
                />
              );
            })}

            {/* Empty state */}
            {paginatedBauvorhaben.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant='body1' align='center' sx={{py: 4}}>
                      {searchTerm ? `${t('no_results_found')}` : `${t('no_new_projects_available')}`}
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>

          {/* Pagination controls */}
          <Box sx={{mt: 2}}>
            <BauvorhabenPaginationControls
              page={page}
              itemsPerPage={itemsPerPage}
              totalItems={sortedBauvorhaben.length}
              totalPages={totalPages}
              onPrevPage={handlePrevPage}
              onNextPage={handleNextPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <DeleteBauvorhabenDialog
        open={open}
        error={error}
        name={name}
        onClose={handleClose}
        onConfirmDelete={handleDelete}
      />

      {/* New Bauvorhaben Modal */}
      <Dialog
        open={openNewModal}
        onClose={handleCloseNewModal}
        maxWidth='sm'
        fullWidth
        aria-labelledby='new-bauvorhaben-title'>
        <DialogTitle id='new-bauvorhaben-title'>{t('add_project')}</DialogTitle>
        <DialogContent>
          <BauvorhabenAnlegen onClose={handleCloseNewModal} />
        </DialogContent>
      </Dialog>
    </>
  );
}
